import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Search, X, Building2, Wrench, FileText, UserCheck, Users, Package, Bell, BookOpen, Phone, ClipboardList, FolderOpen, FileBadge2 } from 'lucide-react';

const ICONS = {
  building: Building2, asset: Wrench, complaint: FileText, tenant: UserCheck,
  staff: Users, supplier: Package, notice: Bell, sop: BookOpen, contact: Phone,
  purchase_order: ClipboardList, vault_document: FolderOpen, ppm_task: ClipboardList, certificate: FileBadge2,
};

const ROUTES = {
  building: '/buildings', asset: '/assets', complaint: '/complaints',
  tenant: '/tenants', staff: '/staff', supplier: '/suppliers',
  notice: '/notices', sop: '/sops', contact: '/contacts',
  purchase_order: '/purchase-orders', vault_document: '/vault', ppm_task: '/ppm-tasks', certificate: '/certificates',
};

export default function GlobalSearch({ compact = false }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const boxRef = useRef(null);

  useEffect(() => {
    if (!query || query.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/search?q=${encodeURIComponent(query)}`, { withCredentials: true });
        setResults(r.data.results || []);
      } catch { setResults([]); }
      setLoading(false);
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    const handler = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const pick = (r) => {
    const route = ROUTES[r.type];
    if (route) navigate(route);
    setOpen(false);
    setQuery('');
  };

  const label = (r) => {
    const i = r.item;
    if (r.type === 'building') return i.name;
    if (r.type === 'asset') return `${i.name} (${i.asset_type})`;
    if (r.type === 'complaint') return `${i.ticket_number ? '[' + i.ticket_number + '] ' : ''}${i.title} · ${i.status}`;
    if (r.type === 'tenant') return `${i.tenant_name} · Unit ${i.unit_number}`;
    if (r.type === 'staff') return `${i.name} · ${i.employee_type}`;
    if (r.type === 'supplier') return i.name;
    if (r.type === 'notice') return `${i.title} · ${i.category}`;
    if (r.type === 'sop') return `${i.title} · ${i.category}`;
    if (r.type === 'contact') return `${i.name} · ${i.phone || ''}`;
    if (r.type === 'purchase_order') return `LPO ${i.po_number} · ${i.supplier_name}`;
    if (r.type === 'vault_document') return `${i.title} · ${i.category}`;
    if (r.type === 'ppm_task') return `${i.title} · ${i.status}`;
    if (r.type === 'certificate') return `${i.name} · expires ${i.expiry_date || '—'}`;
    return '';
  };

  return (
    <div ref={boxRef} className={`relative ${compact ? 'w-full' : 'w-full max-w-md'}`}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          data-testid="global-search-input"
          type="text"
          placeholder="Search ticket #, buildings, complaints, staff…"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          className="w-full pl-10 pr-9 py-2 bg-slate-100 border border-transparent focus:border-teal-500 focus:bg-white rounded-xl text-sm outline-none transition"
        />
        {query && (
          <button data-testid="clear-search-btn" onClick={() => { setQuery(''); setResults([]); }} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-200 rounded">
            <X className="w-3.5 h-3.5 text-slate-500" />
          </button>
        )}
      </div>
      {open && query.length >= 2 && (
        <div data-testid="search-dropdown" className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border border-slate-200 max-h-96 overflow-y-auto z-50">
          {loading && <div className="p-3 text-sm text-muted-foreground text-center">Searching…</div>}
          {!loading && results.length === 0 && <div className="p-3 text-sm text-muted-foreground text-center">No results</div>}
          {!loading && results.map((r, idx) => {
            const Icon = ICONS[r.type] || Search;
            return (
              <button
                key={idx}
                data-testid={`search-result-${idx}`}
                onClick={() => pick(r)}
                className="w-full px-3 py-2.5 hover:bg-teal-50 flex items-center gap-3 border-b border-slate-50 last:border-none text-left"
              >
                <div className="p-1.5 rounded-lg bg-slate-100"><Icon className="w-3.5 h-3.5 text-slate-600" /></div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">{label(r)}</div>
                  <div className="text-[10px] uppercase tracking-wide text-slate-400">{r.type}</div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
